import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const organizationApi = {
  // ADR-096: the report skeleton — cluster → BU → Project → Team with owners and
  // per-project headcounts, no employee rows.
  // ADR-103: pass asOfDate (YYYY-MM-DD) for the structure as it was on that
  // date — the response is then an envelope { asOfDate, historyStartsAt,
  // hierarchy } instead of a bare array. Pre-backfill dates return 400.
  // ADR-120: `includeInactive` keeps Business Units that had already ended on the
  // reference date (they are pruned by default, like ended projects).
  getHierarchy: async (asOfDate = null, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (asOfDate) params.set('asOfDate', asOfDate);
      if (options.includeInactive) params.set('includeInactive', 'true');
      const query = params.toString() ? `?${params.toString()}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/organization/hierarchy${query}`);
    } catch (error) {
      // Keep the body: a pre-history 400 carries historyStartsAt (ADR-103).
      throw new ApiError('Failed to fetch organization hierarchy', error.status || 500, error.data);
    }
  },

  // ADR-103: the earliest date structure history covers ({ historyStartsAt }),
  // null before the backfill — clamps the report's as-of picker up front.
  getHierarchyHistoryStart: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/organization/hierarchy/history-start`);
    } catch (error) {
      throw new ApiError('Failed to fetch structure history start date', error.status || 500);
    }
  },

  // ADR-096: one project's current + upcoming allocations, fetched when the
  // Organization Hierarchy report expands the project node.
  // ADR-103: with asOfDate, the allocations ACTIVE on that date.
  getProjectAllocations: async (projectId, asOfDate = null) => {
    try {
      const query = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/organization/projects/${projectId}/allocations${query}`);
    } catch (error) {
      throw new ApiError('Failed to fetch project allocations', error.status || 500);
    }
  },

  // ADR-096 Amendment 3: one individual's active allocation refs
  // (projectId, teamId), for the report's Individual filter.
  getIndividualAllocations: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/organization/individuals/${employeeId}/allocations`);
    } catch (error) {
      throw new ApiError('Failed to fetch individual allocations', error.status || 500);
    }
  },

  // ADR-104: counts for the Unallocated node — fixed-size payload (one row per
  // cluster + one null row), fetched on mount in parallel with the skeleton.
  getUnallocatedSummary: async (asOfDate = null) => {
    try {
      const query = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/organization/unallocated/summary${query}`);
    } catch (error) {
      throw new ApiError('Failed to fetch the unallocated summary', error.status || 500);
    }
  },

  // ADR-104: the Unallocated node's rows, fetched when the node is expanded.
  getUnallocatedIndividuals: async (asOfDate = null) => {
    try {
      const query = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      return await authenticatedApi.get(`${API_BASE_URL}/organization/unallocated${query}`);
    } catch (error) {
      throw new ApiError('Failed to fetch unallocated individuals', error.status || 500);
    }
  },

  getLegend: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/organization/legend`);
    } catch (error) {
      throw new ApiError('Failed to fetch organization legend', error.status || 500);
    }
  },
  
  getStructure: async (orgUnitTypeId) => {
    try {
      const url = orgUnitTypeId 
        ? `${API_BASE_URL}/organization/structure?orgUnitTypeId=${orgUnitTypeId}`
        : `${API_BASE_URL}/organization/structure`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch organization structure', error.status || 500);
    }
  },

  // Departments endpoints
  /*
  getDepartments: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/departments`);
    } catch (error) {
      throw new ApiError('Failed to fetch departments', error.status || 500);
    }
  },
  */

  /*
  getDepartmentHierarchy: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/department-hierarchy`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to fetch department hierarchy', error.status || 500);
    }
  },
  */

  /*
  getDepartmentById: async (departmentId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/departments/${departmentId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department not found', 404);
      }
      throw new ApiError('Failed to fetch department', error.status || 500);
    }
  },
  */

  /*
  createDepartment: async (departmentData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/departments`, departmentData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid department data', 400);
      }
      throw new ApiError('Failed to create department', error.status || 500);
    }
  },
  */

  /*
  updateDepartment: async (departmentId, departmentData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/departments/${departmentId}`, departmentData);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid department data', 400);
      }
      throw new ApiError('Failed to update department', error.status || 500);
    }
  },
  */

  /*
  deleteDepartment: async (departmentId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/departments/${departmentId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid department data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department not found', 404);
      }
      throw new ApiError('Failed to delete department', error.status || 500);
    }
  },
  */

  // Business Units endpoints
  // ADR-120: selection lists exclude ended units; pass { includeInactive: true }
  // on history / report surfaces.
  getBusinessUnits: async (options = {}) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units${options.includeInactive ? '?includeInactive=true' : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch business units', error.status || 500);
    }
  },

  getBusinessUnit: async (id) => {
    try {
      if (!id) {
        throw new ApiError('Business Unit ID is required', 400);
      }
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Business Unit not found', 404);
      }
      if (error.status === 403) {
        throw new ApiError('Access denied', 403);
      }
      throw new ApiError('Failed to fetch business unit', error.status || 500);
    }
  },

  getBusinessUnitHierarchy: async (clusterId, options = {}) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/business-unit-hierarchy${options.includeInactive ? '?includeInactive=true' : ''}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Cluster not found', 404);
      }
      throw new ApiError('Failed to fetch business unit hierarchy', error.status || 500);
    }
  },

  getAllBusinessUnitsHierarchy: async (options = {}) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/hierarchy${options.includeInactive ? '?includeInactive=true' : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch business units hierarchy', error.status || 500);
    }
  },

  createBusinessUnit: async (businessUnitData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/business-units`, businessUnitData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid business unit data', 400);
      }
      throw new ApiError('Failed to create business unit', error.status || 500);
    }
  },

  updateBusinessUnit: async (businessUnitId, businessUnitData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/business-units/${businessUnitId}`, businessUnitData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid business unit data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Business unit not found', 404);
      }
      throw new ApiError('Failed to update business unit', error.status || 500);
    }
  },

  deleteBusinessUnit: async (businessUnitId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/business-units/${businessUnitId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Business unit not found', 404);
      }
      throw new ApiError('Failed to delete business unit', error.status || 500);
    }
  },

  // Clients endpoints
  getClients: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clients`);
    } catch (error) {
      throw new ApiError('Failed to fetch clients', error.status || 500);
    }
  },

  createClient: async (clientData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/clients`, clientData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid client data', 400);
      }
      throw new ApiError('Failed to create client', error.status || 500);
    }
  },

  updateClient: async (clientId, clientData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/clients/${clientId}`, clientData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid client data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Company not found', 404);
      }
      throw new ApiError('Failed to update client', error.status || 500);
    }
  },

  deleteClient: async (clientId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/clients/${clientId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Client not found', 404);
      }
      throw new ApiError('Failed to delete client', error.status || 500);
    }
  },

  // Companies endpoints
  getCompanies: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/companies`);
    } catch (error) {
      throw new ApiError('Failed to fetch companies', error.status || 500);
    }
  },

  createCompany: async (companyData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/companies`, companyData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid company data', 400);
      }
      throw new ApiError('Failed to create company', error.status || 500);
    }
  },

  updateCompany: async (companyId, companyData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/companies/${companyId}`, companyData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid company data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Company not found', 404);
      }
      throw new ApiError('Failed to update company', error.status || 500);
    }
  },

  deleteCompany: async (companyId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/companies/${companyId}`);
    } catch (error) {
      if (error.status === 400  ) {
        throw new ApiError(error.data?.error || 'Invalid company data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Company not found', 404);
      }
      throw new ApiError('Failed to delete company', error.status || 500);
    }
  },

  // Countries endpoints
  getCountries: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/countries`);
    } catch (error) {
      throw new ApiError('Failed to fetch countries', error.status || 500);
    }
  },

  createCountry: async (countryData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/countries`, countryData);
    } catch (error) {
      throw new ApiError('Failed to create country', error.status || 500);
    }
  },

  updateCountry: async (countryId, countryData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/countries/${countryId}`, countryData);
    } catch (error) {
      throw new ApiError('Failed to update country', error.status || 500);
    }
  },

  deleteCountry: async (countryId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/countries/${countryId}`);
    } catch (error) {
      throw new ApiError('Failed to delete country', error.status || 500);
    }
  },

  getCountryById: async (countryId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/countries/${countryId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch country', error.status || 500);
    }
  },

  getBusinessAreas: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-areas`);
    } catch (error) {
      throw new ApiError('Failed to fetch business areas', error.status || 500);
    }
  },

  createBusinessArea: async (businessAreaData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/business-areas`, businessAreaData);
    } catch (error) {
      throw new ApiError('Failed to create business area', error.status || 500);
    }
  },

  updateBusinessArea: async (businessAreaId, businessAreaData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/business-areas/${businessAreaId}`, businessAreaData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid business area data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Business area not found', 404);
      }
      throw new ApiError('Failed to update business area', error.status || 500);
    }
  },

  deleteBusinessArea: async (businessAreaId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/business-areas/${businessAreaId}`);
    } catch (error) {
      throw new ApiError('Failed to delete business area', error.status || 500);
    }
  },

  getContractTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/contract-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch contract types', error.status || 500);
    }
  },

  createContractType: async (contractTypeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/contract-types`, contractTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid contract type data', 400);
      }
      throw new ApiError('Failed to create contract type', error.status || 500);
    }
  },

  updateContractType: async (contractTypeId, contractTypeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/contract-types/${contractTypeId}`, contractTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid contract type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Contract type not found', 404);
      }
      throw new ApiError('Failed to update contract type', error.status || 500);
    }
  },

  deleteContractType: async (contractTypeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/contract-types/${contractTypeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Contract type not found', 404);
      }
      throw new ApiError('Failed to delete contract type', error.status || 500);
    }
  },

  // Clusters endpoints
  getClusters: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters`);
    } catch (error) {
      throw new ApiError('Failed to fetch clusters', error.status || 500);
    }
  },

  createCluster: async (clusterData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/clusters`, clusterData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid cluster data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to create cluster', error.status || 500);
    }
  },

  updateCluster: async (clusterId, clusterData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/clusters/${clusterId}`, clusterData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid cluster data', 400, { code: error.data?.code });
      }
      // ADR-167 D6: a lowering below what people hold — the body lists them
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'The change conflicts with existing contracts', 409, {
          code: error.data?.code,
          people: error.data?.people || [],
          newLimit: error.data?.newLimit
        });
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to update cluster', error.status || 500);
    }
  },

  // ADR-124: the 400/409 body carries a `blockers` array — pass it through as
  // the ApiError's data so the delete dialog can list every reason, instead of
  // collapsing it to one sentence in a toast.
  deleteCluster: async (clusterId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/clusters/${clusterId}`);
    } catch (error) {
      if (error.status === 400 || error.status === 409) {
        throw new ApiError(
          error.data?.error || 'Cannot delete this cluster.',
          error.status,
          { blockers: error.data?.blockers || [] }
        );
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to delete cluster', error.status || 500);
    }
  },

  // ADR-124: what blocks this cluster's deletion, or what deleting it erases.
  getClusterDeletePreview: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/delete-preview`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to check whether this cluster can be deleted', error.status || 500);
    }
  },

  // ADR-167 D6/D8: who would be above the limit if the Region were set to `value` ('' = the default)
  getClusterOpenContractLimitImpact: async (clusterId, value) => {
    try {
      const query = value === null || value === undefined ? '' : `?value=${encodeURIComponent(value)}`;
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/open-contract-limit-impact${query}`);
    } catch (error) {
      if (error.status === 400 || error.status === 404) {
        throw new ApiError(error.data?.error || 'Cannot preview this change', error.status);
      }
      throw new ApiError('Failed to preview the open-contract limit change', error.status || 500);
    }
  },

  getClusterById: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}`);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to fetch cluster', error.status || 500);
    }
  },

  getBusinessUnitsByClusterId: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/business-units`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid cluster data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Cluster not found', 404);
      }
      throw new ApiError('Failed to fetch business units for cluster', error.status || 500);
    }
  },

  // Employee Contract Types endpoints
  getEmployeeContractTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-contract-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee contract types', error.status || 500);
    }
  },

  createEmployeeContractType: async (contractTypeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employee-contract-types`, contractTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee contract type data', 400);
      }
      throw new ApiError('Failed to create employee contract type', error.status || 500);
    }
  },

  updateEmployeeContractType: async (contractTypeId, contractTypeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employee-contract-types/${contractTypeId}`, contractTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee contract type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee contract type not found', 404);
      }
      throw new ApiError('Failed to update employee contract type', error.status || 500);
    }
  },

  deleteEmployeeContractType: async (contractTypeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employee-contract-types/${contractTypeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee contract type not found', 404);
      }
      throw new ApiError('Failed to delete employee contract type', error.status || 500);
    }
  },

  // Employee Engagement Types endpoints
  getEmployeeEngagementTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-engagement-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee engagement types', error.status || 500);
    }
  },

  createEmployeeEngagementType: async (engagementTypeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employee-engagement-types`, engagementTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee engagement type data', 400);
      }
      throw new ApiError('Failed to create employee engagement type', error.status || 500);
    }
  },

  updateEmployeeEngagementType: async (engagementTypeId, engagementTypeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employee-engagement-types/${engagementTypeId}`, engagementTypeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee engagement type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee engagement type not found', 404);
      }
      throw new ApiError('Failed to update employee engagement type', error.status || 500);
    }
  },

  deleteEmployeeEngagementType: async (engagementTypeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employee-engagement-types/${engagementTypeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee engagement type not found', 404);
      }
      throw new ApiError('Failed to delete employee engagement type', error.status || 500);
    }
  },

  // Employee Engagement Models endpoints
  getEmployeeEngagementModels: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/employee-engagement-models`);
    } catch (error) {
      throw new ApiError('Failed to fetch employee engagement models', error.status || 500);
    }
  },

  createEmployeeEngagementModel: async (engagementModelData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/employee-engagement-models`, engagementModelData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee engagement model data', 400);
      }
      throw new ApiError('Failed to create employee engagement model', error.status || 500);
    }
  },

  updateEmployeeEngagementModel: async (engagementModelId, engagementModelData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/employee-engagement-models/${engagementModelId}`, engagementModelData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee engagement model data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Employee engagement model not found', 404);
      }
      throw new ApiError('Failed to update employee engagement model', error.status || 500);
    }
  },

  deleteEmployeeEngagementModel: async (engagementModelId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/employee-engagement-models/${engagementModelId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee engagement model not found', 404);
      }
      throw new ApiError('Failed to delete employee engagement model', error.status || 500);
    }
  },

  // Department Types endpoints
  /*
  getDepartmentType: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/department-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Department type not found', 404);
      }
      throw new ApiError('Failed to fetch department type', error.status || 500);
    }
  },
  */

  /*
  getDepartmentTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/department-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch department types', error.status || 500);
    }
  },
  */

  /*
  createDepartmentType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/department-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid department type data', 400, error.data);
      }
      throw new ApiError('Failed to create department type', error.status || 500);
    }
  },
  */

  /*
  updateDepartmentType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/department-types/${id}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Department type not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid department type data', 400, error.data);
      }
      throw new ApiError('Failed to update department type', error.status || 500);
    }
  },
  */

  /*
  deleteDepartmentType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/department-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Department type not found', 404);
      }
      throw new ApiError('Failed to delete department type', error.status || 500);
    }
  },
  */

  // Organizational Unit Types endpoints
  getOrgUnitType: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-unit-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Organizational Unit Type not found', 404);
      }
      throw new ApiError('Failed to fetch organizational unit type', error.status || 500);
    }
  },

  getOrgUnitTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-unit-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch organizational unit types', error.status || 500);
    }
  },

  createOrgUnitType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-unit-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Organizational Unit Type with this name already exists', 400);
      }
      throw new ApiError('Failed to create organizational unit type', error.status || 500);
    }
  },

  updateOrgUnitType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/org-unit-types/${id}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Organizational Unit Type not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Organizational Unit Type with this name already exists', 400);
      }
      throw new ApiError('Failed to update organizational unit type', error.status || 500);
    }
  },

  deleteOrgUnitType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/org-unit-types/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete Organizational Unit Type', 400);
      }
      throw new ApiError('Failed to delete organizational unit type', error.status || 500);
    }
  },

  // Organizational Structure Types (ADR-119) — classification of Clusters
  getOrgStructureTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-structure-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch organizational structure types', error.status || 500);
    }
  },

  getOrgStructureTypeById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-structure-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Organizational Structure Type not found', 404);
      }
      throw new ApiError('Failed to fetch organizational structure type', error.status || 500);
    }
  },

  createOrgStructureType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-structure-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Organizational Structure Type with this name already exists', 400);
      }
      throw new ApiError('Failed to create organizational structure type', error.status || 500);
    }
  },

  updateOrgStructureType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/org-structure-types/${id}`, data);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Organizational Structure Type not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Organizational Structure Type with this name already exists', 400);
      }
      throw new ApiError('Failed to update organizational structure type', error.status || 500);
    }
  },

  deleteOrgStructureType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/org-structure-types/${id}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot delete Organizational Structure Type', 400);
      }
      throw new ApiError('Failed to delete organizational structure type', error.status || 500);
    }
  },

  // Position-Role Management
  attachRoleToPosition: async (positionId, roleId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-positions/attach-role`, { positionId, roleId });
    } catch (error) {
      throw new ApiError('Failed to attach role to position', error.status || 500);
    }
  },

  detachRoleFromPosition: async (positionId, roleId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-positions/detach-role`, { positionId, roleId });
    } catch (error) {
      throw new ApiError('Failed to detach role from position', error.status || 500);
    }
  },

  // Grades
  getAllGrades: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-grades`);
    } catch (error) {
      throw new ApiError('Failed to fetch grades', error.status || 500);
    }
  },

  getGradeById: async (gradeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-grades/${gradeId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch grade', error.status || 500);
    }
  },

  createGrade: async (gradeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-grades`, gradeData);
    } catch (error) {
      throw new ApiError('Failed to create grade', error.status || 500);
    }
  },

  updateGrade: async (gradeId, gradeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/org-grades/${gradeId}`, gradeData);
    } catch (error) {
      throw new ApiError('Failed to update grade', error.status || 500);
    }
  },

  deleteGrade: async (gradeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/org-grades/${gradeId}`);
    } catch (error) {
      throw new ApiError('Failed to delete grade', error.status || 500);
    }
  },

  // Attach employee to DEFAULT_INTERNAL project
  attachEmployeeToDefaultInternal: async (businessUnitId, employeeId, managerId) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/business-units/${businessUnitId}/employees/${employeeId}/managers/${managerId}/attach-to-default-internal`
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Business unit or employee not found', 404);
      }
      throw new ApiError('Failed to attach employee to default internal project', error.status || 500);
    }
  },

  /*
  attachEmployeeToDepartment: async (departmentId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/departments/${departmentId}/employees`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid employee data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department not found', 404);
      }
      throw new ApiError('Failed to attach employee to department', error.status || 500);
    }
  },
  */

  /*
  updateEmployeeDepartmentRoles: async (departmentId, employeeId, roleIds) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/departments/${departmentId}/employees/${employeeId}/roles`,
        { roleIds }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid role data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department or employee not found', 404);
      }
      throw new ApiError('Failed to update employee roles', error.status || 500);
    }
  },
  */

  // Positions
  getAllPositions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-positions`);
    } catch (error) {
      throw new ApiError('Failed to fetch positions', error.status || 500);
    }
  },

  getPositionsByOrgUnitType: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-positions/by-org-unit-type`);
    } catch (error) {
      throw new ApiError('Failed to fetch positions by org unit type', error.status || 500);
    }
  },

  getPositionRoles: async (positionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-positions/${positionId}/roles`);
    } catch (error) {
      throw new ApiError('Failed to fetch position roles', error.status || 500);
    }
  },

  getPositionById: async (positionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-positions/${positionId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch position', error.status || 500);
    }
  },

  createPosition: async (positionData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-positions`, positionData);
    } catch (error) {
      throw new ApiError('Failed to create position', error.status || 500);
    }
  },

  updatePosition: async (positionId, positionData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/org-positions/${positionId}`, positionData);
    } catch (error) {
      throw new ApiError('Failed to update position', error.status || 500);
    }
  },

  deletePosition: async (positionId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/org-positions/${positionId}`);
    } catch (error) {
      throw new ApiError('Failed to delete position', error.status || 500);
    }
  },

  // Roles
  getAllRoles: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-roles`);
    } catch (error) {
      throw new ApiError('Failed to fetch roles', error.status || 500);
    }
  },

  getRoleById: async (roleId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-roles/${roleId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch role', error.status || 500);
    }
  },

  createRole: async (roleData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/org-roles`, roleData);
    } catch (error) {
      throw new ApiError('Failed to create role', error.status || 500);
    }
  },

  updateRole: async (roleId, roleData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/org-roles/${roleId}`, roleData);
    } catch (error) {
      throw new ApiError('Failed to update role', error.status || 500);
    }
  },

  deleteRole: async (roleId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/org-roles/${roleId}`);
    } catch (error) {
      throw new ApiError('Failed to delete role', error.status || 500);
    }
  },

  // Delivery positions
  getDeliveryPositions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/org-positions/delivery`);
    } catch (error) {
      throw new ApiError('Failed to fetch delivery positions', error.status || 500);
    }
  },

  getCompaniesByClusterId: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/companies`);
    } catch (error) {
      throw new ApiError('Failed to fetch companies for cluster', error.status || 500);
    }
  },

  /*
  endEmployeeDepartment: async (departmentId, employeeId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/departments/${departmentId}/employees/${employeeId}/end`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid end date', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department or employee not found', 404);
      }
      throw new ApiError('Failed to end department assignment', error.status || 500);
    }
  },
  */

  /*
  moveEmployeeToDepartment: async (departmentId, employeeId, newDepartmentId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/departments/move-employee`, {
        departmentId,
        employeeId,
        newDepartmentId
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid move request', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department or employee not found', 404);
      }
      throw new ApiError('Failed to move employee', error.status || 500);
    }
  },
  */

  /**
   * ADR-118: move a business unit within or across Clusters. The server
   * resolves the kind; `effectiveDate`, `confirmation` and `reason` only matter
   * for a transfer.
   */
  moveBusinessUnit: async (sourceBusinessUnitId, destinationBusinessUnitId, options = {}) => {
    const { effectiveDate, confirmation, reason } = options;
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/business-units/move`, {
        sourceBusinessUnitId,
        destinationBusinessUnitId,
        effectiveDate,
        confirmation,
        reason
      });
    } catch (error) {
      if (error.status === 400 || error.status === 409) {
        throw new ApiError(error.data?.error || 'Invalid move request', error.status);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to move this business unit', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Business unit not found', 404);
      }
      throw new ApiError('Failed to move business unit', error.status || 500);
    }
  },

  /** ADR-118: dry run — kind, impact, requirements, blocked reason. */
  getBusinessUnitMovePreview: async (sourceBusinessUnitId, destinationBusinessUnitId) => {
    const query = `?sourceBusinessUnitId=${encodeURIComponent(sourceBusinessUnitId)}&destinationBusinessUnitId=${encodeURIComponent(destinationBusinessUnitId)}`;
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/move-preview${query}`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to preview the move', error.status || 500);
    }
  },

  /** ADR-118: cross-cluster move policy + whether the caller may transfer at all. */
  getBusinessUnitMoveRules: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/move-rules`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to load move rules', error.status || 500);
    }
  },

  /** ADR-120: lifecycle policy + whether the caller may end / reactivate units at all. */
  getBusinessUnitLifecycleRules: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/lifecycle-rules`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to load lifecycle rules', error.status || 500);
    }
  },

  /** ADR-120: dry run of ending a unit — blockers, impact, requirements. */
  getBusinessUnitInactivationPreview: async (businessUnitId, options = {}) => {
    const params = new URLSearchParams();
    if (options.endDate) params.set('endDate', options.endDate);
    if (options.cascade) params.set('cascade', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/${businessUnitId}/inactivation-preview${query}`);
    } catch (error) {
      throw new ApiError(error.data?.error || 'Failed to preview the end of this business unit', error.status || 500);
    }
  },

  /** ADR-120: end (inactivate) a unit on `endDate` (default today). */
  inactivateBusinessUnit: async (businessUnitId, options = {}) => {
    const { endDate, reason, cascade, confirmation } = options;
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/business-units/${businessUnitId}/inactivate`, {
        endDate, reason, cascade, confirmation
      });
    } catch (error) {
      if (error.status === 400 || error.status === 409) {
        const apiError = new ApiError(error.data?.error || 'This business unit cannot be ended', error.status);
        apiError.code = error.data?.code;
        apiError.blockers = error.data?.blockers;
        throw apiError;
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to end this business unit', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Business unit not found', 404);
      }
      throw new ApiError('Failed to end business unit', error.status || 500);
    }
  },

  /** ADR-120: reactivate an ended unit. */
  reactivateBusinessUnit: async (businessUnitId, options = {}) => {
    const { reason, confirmation } = options;
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/business-units/${businessUnitId}/reactivate`, { reason, confirmation });
    } catch (error) {
      if (error.status === 400 || error.status === 409) {
        const apiError = new ApiError(error.data?.error || 'This business unit cannot be reactivated', error.status);
        apiError.code = error.data?.code;
        throw apiError;
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to reactivate this business unit', 403);
      }
      throw new ApiError('Failed to reactivate business unit', error.status || 500);
    }
  },
  /*
  moveDepartment: async (sourceDepartmentId, destinationDepartmentId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/departments/move-department`, {
        sourceDepartmentId,
        destinationDepartmentId
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid move request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to move this department', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Department not found', 404);
      }
      throw new ApiError('Failed to move department', error.status || 500);
    }
  },
  */

  getBusinessUnitDetailedHierarchy: async (businessUnitId, forceAsEmployee = false) => {
    try {
      const queryParams = new URLSearchParams();
      if (forceAsEmployee) queryParams.append('forceAsEmployee', forceAsEmployee);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/business-units/${businessUnitId}/detailed-hierarchy${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access this business unit', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Business unit not found', 404);
      }
      throw new ApiError('Failed to fetch business unit detailed hierarchy', error.status || 500);
    }
  },

  getClusterManagerHistory: async (clusterId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/clusters/${clusterId}/manager-history`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Cluster not found', 404);
      }
      throw new ApiError('Failed to fetch cluster manager history', error.status || 500);
    }
  },

  getBusinessUnitOwnerHistory: async (businessUnitId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/business-units/${businessUnitId}/owner-history`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Business unit not found', 404);
      }
      throw new ApiError('Failed to fetch business unit owner history', error.status || 500);
    }
  },
};